import React, { useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { CustomerGroupsFormTyping } from "../../types";
import CustomerMultiTable from "@components/Dashboard/pages/Customers/CustomerGroups/components/CustomerMultiTable";
import MutationCustomerModal from "./MutationCustomerModal";
import { useGetCustomersQuery } from "@redux/features/customers/customersApi";
import { Customer } from "@/types/general";

export default function CustomerSection() {
    const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);

    const { control, clearErrors, formState } = useFormContext<CustomerGroupsFormTyping>();
    const { data: customersOptions = [], isLoading: isCustomersOptionsLoading } =
        useGetCustomersQuery();

    const {
        fields: customerFields,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "customers",
        keyName: "loop_id",
    });

    const customers = useMemo(
        () =>
            customerFields
                .map((customer) => customer.value)
                .filter((customer): customer is Customer.Slim => !!customer?.id),
        [customerFields]
    );

    const addedCustomersIds = useMemo(
        () => new Set(customers.map((customer) => customer.id)),
        [customers]
    );

    const filteredCustomersOptions = useMemo(
        () => customersOptions.filter((customer) => !addedCustomersIds.has(customer.id)),
        [customersOptions, addedCustomersIds]
    );

    const customersError = formState.errors.customers;
    const customersErrorMessage =
        typeof customersError?.message === "string" ? customersError.message : undefined;

    const handleRemoveCustomer = (customer: Customer.Slim) => {
        const customerIndex = customerFields.findIndex((field) => field.value?.id === customer.id);

        if (customerIndex >= 0) {
            remove(customerIndex);
        }
    };

    return (
        <>
            <div>
                <CustomerMultiTable
                    customers={customers}
                    onAddClick={() => setIsAddCustomerModalOpen(true)}
                    onRemoveCustomer={handleRemoveCustomer}
                />

                {customersErrorMessage ? (
                    <div className="mt-[8px] text-sm text-inputs-textError">
                        {customersErrorMessage}
                    </div>
                ) : null}
            </div>

            <MutationCustomerModal
                isOpen={isAddCustomerModalOpen}
                onClose={() => setIsAddCustomerModalOpen(false)}
                customersOptions={filteredCustomersOptions}
                isCustomersOptionsLoading={isCustomersOptionsLoading}
                onCustomerAddition={(customer) => {
                    append({ value: customer });
                    clearErrors("customers");
                    setIsAddCustomerModalOpen(false);
                }}
            />
        </>
    );
}
